import { TableSearchField, useSearchText } from '@components/DataTable';
import { OmniMatchAllToggle, useFindShortcut } from '@components/OmniFilter/TableOmniSearch';
import { SortButton } from '@components/SortSelect/SortButton';
import { ViewModeToggle } from '@components/ViewModeToggle/ViewModeToggle';
import { useLibraryViewMode } from '@components/ViewModeToggle/useViewModePreference';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CreateNewFolderOutlined from '@mui/icons-material/CreateNewFolderOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Tooltip } from '@mui/material';
import { useFolderDialogs } from '@pages/Library/components/Folders/FolderDialogs';
import { useLibraryOmniFilter } from '@pages/Library/components/LibraryOmniFilter/LibraryOmniFilter';
import {
  getSortOptionKey,
  getVisibleSortGroups,
  SORT_OPTION_LABELS,
  SORT_OPTIONS,
} from '@pages/Library/utils/sortOptions';
import { useLibrary } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { trackingStore } from '@stores/Tracking';
import { useUIPreferences } from '@stores/UIPreferences';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { AutoplayToggle } from './AutoplayToggle';
import { LibraryAddMediaButton } from './LibraryAddMediaButton';
import { LibraryStats } from './LibraryStats';
import css from './LibraryToolbar.module.scss';
import { MobileViewOptions } from './MobileViewOptions';

/** The gallery's sort. List view sorts from its column headers instead. */
const LibrarySortButton = observer(() => {
  const folder = useLibraryFolder();
  const currentSortKey = getSortOptionKey(folder.effectiveSortModel);
  const sortGroups = getVisibleSortGroups(folder.hasPublishedAtMedia).map((group) =>
    group.map((option) => ({ value: option.key, label: option.label })),
  );
  return (
    <SortButton
      variant="ghost"
      tooltip="Sort media by"
      value={currentSortKey}
      selectedLabel={SORT_OPTION_LABELS[currentSortKey] || currentSortKey}
      options={sortGroups}
      onChange={(key) => {
        // 'custom' is only a display value (table column sort) — not selectable.
        if (key === 'custom') return;
        const sortModel = SORT_OPTIONS[key];
        if (sortModel) {
          folder.setSortModel(sortModel);
          trackingStore.librarySortChanged({ sortBy: key });
        }
      }}
    />
  );
});

/** White, as the page's lead action. */
const AddMediaButton = () => (
  <LibraryAddMediaButton
    renderTrigger={({ onClick, disabled }) => (
      <button
        type="button"
        className={classNames(css.button, css.primary)}
        aria-label="Add media"
        disabled={disabled}
        onClick={onClick}
      >
        <AddRoundedIcon />
        <span className={css.label}>Add media</span>
      </button>
    )}
  />
);

const NewFolderButton = observer(() => {
  const folder = useLibraryFolder();
  const dialogs = useFolderDialogs();
  return (
    <Tooltip title="New folder" placement="top" arrow>
      <button
        type="button"
        className={css.button}
        aria-label="New folder"
        onClick={() => dialogs.createFolder(folder.currentFolderPath)}
      >
        <CreateNewFolderOutlined />
        <span className={css.label}>New folder</span>
      </button>
    </Tooltip>
  );
});

/**
 * The library's search line, above the folders and the media in both views: Add media and New
 * folder, the search, and what the view holds, then the gallery's sort and autoplay and the view
 * switch on the right. The table sorts from its headers and plays nothing on hover, so it shows
 * neither of those. The header above keeps only Atlas, which leaves the library.
 *
 * The search is the omnifilter (names, YouTube IDs, media IDs; folders by name), so there's one
 * search on screen and no Filters pill. ⌘F / Ctrl+F focuses it.
 *
 * On a phone it's one row: Add media, New folder and a magnifier, with the view, sort and autoplay
 * behind one button at the end (`MobileViewOptions`). The magnifier opens the search across the
 * whole row, with Cancel beside it, and it stays open while it holds a query: squeezed between the
 * buttons, the field was too narrow to read. This replaced the row of circles that floated over
 * the phone's media, which put the search in a sheet behind a Filters glyph.
 */
export const LibraryToolbar = observer(() => {
  const library = useLibrary();
  const uiPreferences = useUIPreferences();
  const viewMode = useLibraryViewMode();
  const isMobile = useIsMobile();
  const omniFilter = useLibraryOmniFilter();
  const [search, changeSearch] = useSearchText(omniFilter.applied, omniFilter.onChange);
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  useFindShortcut(
    () =>
      isMobile
        ? null
        : searchRef.current && {
            focus: () => {
              searchRef.current?.focus();
              searchRef.current?.select();
            },
          },
    omniFilter.onOpen,
  );
  const editable = !library.isReadOnly;
  const gallery = uiPreferences.libraryViewMode === 'grid';

  const searchField = (
    <TableSearchField
      className={css.search}
      inputRef={searchRef}
      value={search}
      onChange={changeSearch}
      onBlur={() => !search.trim() && setSearchOpen(false)}
      placeholder="Search name, YouTube ID, or media ID"
    />
  );
  const matchAll = (
    <div className={css.matchAll}>
      <OmniMatchAllToggle
        query={omniFilter.applied}
        matchAll={omniFilter.matchAll}
        onMatchAllChange={omniFilter.onMatchAllChange}
      />
    </div>
  );

  if (isMobile) {
    const searching = searchOpen || !!search;
    return (
      <div className={css.mobileToolbar}>
        <div className={css.mobileRow}>
          {searching ? (
            <>
              {searchField}
              <button
                type="button"
                className={css.cancel}
                onClick={() => {
                  changeSearch('');
                  setSearchOpen(false);
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {editable && <AddMediaButton />}
              {editable && <NewFolderButton />}
              <button
                type="button"
                className={css.button}
                aria-label="Search"
                onClick={() => {
                  // Rendered and focused in the same tap, or iOS won't raise the keyboard.
                  flushSync(() => setSearchOpen(true));
                  searchRef.current?.focus();
                  omniFilter.onOpen('click');
                }}
              >
                <SearchRoundedIcon />
              </button>
              <div className={css.mobileEnd}>
                <MobileViewOptions buttonClassName={css.button} />
              </div>
            </>
          )}
        </div>
        {matchAll}
      </div>
    );
  }

  return (
    <div className={classNames(css.toolbar, gallery && css.gallery)}>
      {editable && <AddMediaButton />}
      {editable && <NewFolderButton />}
      {searchField}
      <div className={css.stats}>
        <LibraryStats />
      </div>
      {matchAll}
      <div className={css.end}>
        {gallery && <LibrarySortButton />}
        {gallery && <AutoplayToggle />}
        <ViewModeToggle {...viewMode} />
      </div>
    </div>
  );
});
